import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment.development';
import { SocketService } from './socket.service';
import { AuthService } from './auth.service';

export interface Usuario {
  id_usuario: number;
  nombre: string;
  apellido: string;
  nombre_usuario: string;
  email: string;
  foto_perfil?: string;
  nombre_completo?: string;
  es_perfil_publico?: boolean;
}

export interface Mensaje {
  id_mensaje: number;
  id_conversacion: number;
  id_usuario: number;
  contenido: string;
  tipo_mensaje: 'texto' | 'imagen' | 'documento' | 'audio' | 'video' | 'enlace';
  archivo_url?: string;
  nombre_archivo?: string;
  tamaño_archivo?: number;
  fecha_envio: Date;
  editado: boolean;
  fecha_edicion?: Date;
  eliminado: boolean;
  usuario?: Usuario;
}

export interface Participante {
  id_participante: number;
  id_conversacion: number;
  id_usuario: number;
  fecha_union: string;
  silenciado: boolean;
  fecha_ultima_lectura: string | null;
  es_admin: boolean;
  activo: boolean;
  usuario: Usuario;
}

export interface Conversacion {
  id_conversacion: number;
  tipo_conversacion: 'individual' | 'grupo';
  nombre_conversacion?: string | null;
  foto_conversacion?: string | null;
  id_creador?: number;
  fecha_creacion: string;
  fecha_ultimo_mensaje?: string | null;
  activa: boolean;
  participantes: Participante[];
  mensajes?: Mensaje[];
  mensajes_no_leidos?: number;
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private apiUrl = `${environment.apiUrl}/chat`;
  
  // Estados reactivos
  private conversacionesSubject = new BehaviorSubject<Conversacion[]>([]);
  public conversaciones$ = this.conversacionesSubject.asObservable();

  private mensajesSubject = new BehaviorSubject<Mensaje[]>([]);
  public mensajes$ = this.mensajesSubject.asObservable();

  private conversacionActualSubject = new BehaviorSubject<number | null>(null);
  public conversacionActual$ = this.conversacionActualSubject.asObservable();

  private mensajesNoLeidosSubject = new BehaviorSubject<number>(0);
  public mensajesNoLeidos$ = this.mensajesNoLeidosSubject.asObservable();

  private usuariosEscribiendoSubject = new BehaviorSubject<{[key: number]: number[]}>({});
  public usuariosEscribiendo$ = this.usuariosEscribiendoSubject.asObservable();

  private usuariosEnLineaSubject = new BehaviorSubject<Set<number>>(new Set());
  public usuariosEnLinea$ = this.usuariosEnLineaSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private authService: AuthService
  ) {
    // Inicializar listeners de Socket.io cuando se conecte
    this.socketService.conectado$.subscribe(conectado => {
      if (conectado) {
        console.log('✅ Socket conectado, inicializando listeners...');
        this.inicializarEventosSocket();
        // Agregar el usuario actual al conjunto de usuarios en línea
        this.agregarUsuarioActualComoEnLinea();
      }
    });
  }

  // ==================== API REST ====================

  /**
   * Crear conversación
   */
  crearConversacion(
    participantes: number[], 
    tipo: 'individual' | 'grupo' = 'individual', 
    nombre?: string
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/conversacion`, {
      participantes,
      tipo,
      nombre
    }).pipe(
      tap(() => this.cargarConversaciones())
    );
  }

  /**
   * Obtener todas las conversaciones
   */
  obtenerConversaciones(): Observable<any> {
    return this.http.get(`${this.apiUrl}/conversaciones`).pipe(
      tap((response: any) => {
        if (response.ok) {
          this.conversacionesSubject.next(response.conversaciones);
        }
      })
    );
  }

  /**
   * Verificar si existe conversación con un usuario
   */
  verificarConversacionExistente(idUsuarioDestino: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/verificar-conversacion/${idUsuarioDestino}`);
  }

  /**
   * Iniciar conversación con un usuario (crea si no existe o devuelve la existente)
   */
  iniciarConversacionConUsuario(idUsuarioDestino: number): Observable<any> {
    return this.verificarConversacionExistente(idUsuarioDestino).pipe(
      switchMap(response => {
        if (response.existe) {
          return of(response.conversacion);
        } else {
          return this.crearConversacion([idUsuarioDestino], 'individual').pipe(
            map(res => res.conversacion)
          );
        }
      })
    );
  }

  /**
   * Obtener mensajes de una conversación
   */
  obtenerMensajes(idConversacion: number, limite: number = 50): Observable<any> {
    return this.http.get(`${this.apiUrl}/conversacion/${idConversacion}/mensajes`, {
      params: { limite: limite.toString() }
    }).pipe(
      tap((response: any) => {
        if (response.ok) {
          this.mensajesSubject.next(response.mensajes);
        }
      })
    );
  }

  /**
   * Contar mensajes no leídos
   */
  contarMensajesNoLeidos(): Observable<number> {
    return this.http.get<any>(`${this.apiUrl}/mensajes-no-leidos/contar`).pipe(
      map(response => {
        const total = response.total_no_leidos || 0;
        this.mensajesNoLeidosSubject.next(total);
        return total;
      })
    );
  }

  /**
   * Obtener recursos (archivos) de una conversación
   */
  obtenerRecursos(idConversacion: number, limite: number = 30, offset: number = 0, tipo?: 'imagenes' | 'documentos' | 'audios'): Observable<any> {
    let params = new HttpParams()
      .set('limite', limite.toString())
      .set('offset', offset.toString());

    if (tipo) {
      params = params.set('tipo_archivo', tipo);
    }

    return this.http.get(`${this.apiUrl}/conversacion/${idConversacion}/recursos`, { params });
  }

  /**
   * Obtener galería (solo imágenes)
   */
  obtenerGaleria(idConversacion: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/conversacion/${idConversacion}/galeria`);
  }

  /**
   * Obtener documentos
   */
  obtenerDocumentos(idConversacion: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/conversacion/${idConversacion}/documentos`);
  }

  /**
   * Enviar mensaje con archivo (método legacy)
   */
  enviarMensajeArchivo(formData: FormData): Observable<any> {
    return this.http.post(`${this.apiUrl}/mensaje-archivo`, formData);
  }

  /**
   * Subir archivo y crear mensaje en una sola llamada
   * Endpoint: POST /api/chat/mensaje-archivo
   * Según README: Los mensajes de audio NO se envían por Socket.io, solo por REST
   */
  uploadFileMessage(idConversacion: number, archivo: File, contenido?: string): Observable<any> {
    const formData = new FormData();
    // Orden según README: id_conversacion, archivo, contenido (opcional)
    formData.append('id_conversacion', idConversacion.toString());
    formData.append('archivo', archivo);
    if (contenido) {
      formData.append('contenido', contenido);
    }
    // HttpClient maneja automáticamente Content-Type: multipart/form-data con boundary
    // El interceptor agrega automáticamente Authorization: Bearer <token>
    return this.http.post(`${this.apiUrl}/mensaje-archivo`, formData);
  }

  /**
   * Validar tipo de archivo permitido
   */
  validarTipoArchivo(archivo: File): boolean {
    const tiposPermitidos = [
      // Imágenes
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      // Documentos
      'application/pdf',
      // Audio
      'audio/mp3',
      'audio/wav',
      'audio/ogg',
      'audio/m4a',
      'audio/aac',
      'audio/webm', // Formato usado por MediaRecorder
      'audio/webm;codecs=opus', // Codec Opus en WebM
      'audio/webm;codecs=vorbis', // Codec Vorbis en WebM
      'audio/ogg;codecs=opus', // Codec Opus en OGG
      'audio/mp4', // M4A también puede venir como audio/mp4
      // Video
      'video/mp4',
      'video/mov',
      'video/webm'
    ];

    // Verificar si el tipo exacto está en la lista
    if (tiposPermitidos.includes(archivo.type)) {
      return true;
    }

    // Verificar si es un tipo de audio que empieza con "audio/" (más permisivo para codecs)
    if (archivo.type.startsWith('audio/')) {
      return true;
    }

    // Verificar si es un tipo de video que empieza con "video/"
    if (archivo.type.startsWith('video/')) {
      return true;
    }

    return false;
  }

  /**
   * Validar tamaño de archivo (máx 10MB)
   */
  validarTamañoArchivo(archivo: File, maxSizeMB: number = 10): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return archivo.size <= maxSizeBytes;
  }

  /**
   * Obtener tipo de mensaje según el tipo MIME del archivo
   */
  obtenerTipoMensaje(archivo: File): 'texto' | 'imagen' | 'documento' | 'audio' | 'video' | 'enlace' {
    if (archivo.type.startsWith('image/')) {
      return 'imagen';
    } else if (archivo.type === 'application/pdf') {
      return 'documento';
    } else if (archivo.type.startsWith('audio/')) {
      return 'audio';
    } else if (archivo.type.startsWith('video/')) {
      return 'video';
    }
    return 'documento'; // Fallback
  }

  /**
   * Formatear tamaño de archivo para mostrar
   */
  formatearTamañoArchivo(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Buscar usuarios para iniciar conversación
   */
  buscarUsuarios(query: string, limite: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('q', query)
      .set('limite', limite.toString());
    
    return this.http.get(`${environment.apiUrl}/perfil/buscar`, { params });
  }

  /**
   * Salir de una conversación
   */
  salirDeConversacion(idConversacion: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/conversacion/${idConversacion}/salir`).pipe(
      tap(() => this.cargarConversaciones())
    );
  }

  // ==================== SOCKET.IO ====================

  private listenersInicializados = false;

  /**
   * Inicializar eventos de Socket.io
   */
  private inicializarEventosSocket(): void {
    // Evitar inicializar múltiples veces
    if (this.listenersInicializados) {
      console.log('⚠️ Listeners ya inicializados, omitiendo...');
      return;
    }

    console.log('🎧 Inicializando listeners de Socket.io...');
    this.listenersInicializados = true;

    // Nuevo mensaje recibido
    this.socketService.escuchar<Mensaje>('nuevo:mensaje').subscribe(mensaje => {
      console.log('💬 Nuevo mensaje recibido vía Socket.io:', mensaje);
      console.log('💬 Tipo de mensaje:', mensaje.tipo_mensaje);
      
      // Verificar que el mensaje pertenezca a la conversación actual
      const conversacionActual = this.conversacionActualSubject.value;
      if (conversacionActual && mensaje.id_conversacion === conversacionActual) {
        // Agregar el nuevo mensaje al array actual
        const mensajesActuales = this.mensajesSubject.value;
        
        // Evitar duplicados
        const existeMensaje = mensajesActuales.some(m => m.id_mensaje === mensaje.id_mensaje);
        if (!existeMensaje) {
          const nuevosMensajes = [...mensajesActuales, mensaje];
          
          console.log('📨 Actualizando mensajes:', mensajesActuales.length, '→', nuevosMensajes.length);
          this.mensajesSubject.next(nuevosMensajes);
        } else {
          console.log('⚠️ Mensaje duplicado, ignorando:', mensaje.id_mensaje);
        }
      } else {
        console.log('⚠️ Mensaje de otra conversación, ignorando. Actual:', conversacionActual, 'Mensaje:', mensaje.id_conversacion);
      }
      
      // Actualizar lista de conversaciones siempre
      this.cargarConversaciones();
    });

    // Mensaje editado
    this.socketService.escuchar<any>('mensaje:editado').subscribe(data => {
      console.log('✏️ Mensaje editado:', data);
      const mensajes = this.mensajesSubject.value;
      const index = mensajes.findIndex(m => m.id_mensaje === data.id_mensaje);
      if (index !== -1) {
        mensajes[index].contenido = data.contenido;
        mensajes[index].editado = true;
        mensajes[index].fecha_edicion = data.fecha_edicion;
        this.mensajesSubject.next([...mensajes]);
      }
    });

    // Mensaje eliminado
    this.socketService.escuchar<any>('mensaje:eliminado').subscribe(data => {
      console.log('🗑️ Mensaje eliminado:', data);
      const mensajes = this.mensajesSubject.value;
      const index = mensajes.findIndex(m => m.id_mensaje === data.id_mensaje);
      if (index !== -1) {
        mensajes[index].eliminado = true;
        mensajes[index].contenido = 'Mensaje eliminado';
        this.mensajesSubject.next([...mensajes]);
      }
    });

    // Usuario escribiendo
    this.socketService.escuchar<any>('usuario:escribiendo').subscribe(data => {
      const escribiendo = this.usuariosEscribiendoSubject.value;
      if (!escribiendo[data.id_conversacion]) {
        escribiendo[data.id_conversacion] = [];
      }
      if (!escribiendo[data.id_conversacion].includes(data.id_usuario)) {
        escribiendo[data.id_conversacion].push(data.id_usuario);
      }
      this.usuariosEscribiendoSubject.next({...escribiendo});
    });

    // Usuario dejó de escribir
    this.socketService.escuchar<any>('usuario:dejo:escribir').subscribe(data => {
      const escribiendo = this.usuariosEscribiendoSubject.value;
      if (escribiendo[data.id_conversacion]) {
        escribiendo[data.id_conversacion] = escribiendo[data.id_conversacion]
          .filter(id => id !== data.id_usuario);
        this.usuariosEscribiendoSubject.next({...escribiendo});
      }
    });

    // Mensajes leídos
    this.socketService.escuchar<any>('mensajes:leidos').subscribe(() => {
      this.contarMensajesNoLeidos().subscribe();
    });

    // Usuario conectado (evento del backend: usuario:online)
    this.socketService.escuchar<any>('usuario:online').subscribe(data => {
      console.log('🟢 Usuario conectado:', data);
      const usuariosEnLinea = this.usuariosEnLineaSubject.value;
      usuariosEnLinea.add(data.id_usuario);
      this.usuariosEnLineaSubject.next(new Set(usuariosEnLinea));
    });

    // Usuario desconectado (evento del backend: usuario:offline)
    this.socketService.escuchar<any>('usuario:offline').subscribe(data => {
      console.log('⚪ Usuario desconectado:', data);
      const usuariosEnLinea = this.usuariosEnLineaSubject.value;
      usuariosEnLinea.delete(data.id_usuario);
      this.usuariosEnLineaSubject.next(new Set(usuariosEnLinea));
    });
  }

  /**
   * Unirse a todas las conversaciones
   */
  unirseAConversaciones(): void {
    this.socketService.emitir('unirse:conversaciones');
    // Cargar estado inicial de usuarios desde HTTP
    this.cargarEstadosUsuariosEnLinea();
  }

  /**
   * Unirse a una conversación específica
   */
  unirseAConversacion(idConversacion: number): void {
    this.conversacionActualSubject.next(idConversacion);
    this.socketService.emitir('unirse:conversacion', { id_conversacion: idConversacion });
  }

  /**
   * Enviar mensaje por Socket.io
   */
  enviarMensaje(
    idConversacion: number,
    contenido: string,
    tipo: 'texto' | 'imagen' | 'documento' | 'audio' | 'video' | 'enlace' = 'texto',
    archivoUrl?: string,
    nombreArchivo?: string,
    tamañoArchivo?: number
  ): void {
    this.socketService.emitir('enviar:mensaje', {
      id_conversacion: idConversacion,
      contenido,
      tipo_mensaje: tipo,
      archivo_url: archivoUrl,
      nombre_archivo: nombreArchivo,
      tamaño_archivo: tamañoArchivo
    });
  }

  /**
   * Subir archivo para el chat
   */
  subirArchivo(archivo: File): Observable<any> {
    const formData = new FormData();
    formData.append('archivo', archivo);

    return this.http.post(`${this.apiUrl}/subir-archivo`, formData);
  }

  /**
   * Editar mensaje
   */
  editarMensaje(idMensaje: number, nuevoContenido: string): void {
    this.socketService.emitir('editar:mensaje', {
      id_mensaje: idMensaje,
      nuevo_contenido: nuevoContenido
    });
  }

  /**
   * Eliminar mensaje
   */
  eliminarMensaje(idMensaje: number): void {
    this.socketService.emitir('eliminar:mensaje', {
      id_mensaje: idMensaje
    });
  }

  /**
   * Indicar que estás escribiendo
   */
  escribiendo(idConversacion: number): void {
    this.socketService.emitir('escribiendo', { id_conversacion: idConversacion });
  }

  /**
   * Indicar que dejaste de escribir
   */
  dejoEscribir(idConversacion: number): void {
    this.socketService.emitir('dejo:escribir', { id_conversacion: idConversacion });
  }

  /**
   * Marcar mensajes como leídos
   */
  marcarComoLeido(idConversacion: number, idMensaje?: number): void {
    this.socketService.emitir('marcar:leido', {
      id_conversacion: idConversacion,
      id_mensaje: idMensaje
    });
  }

  // ==================== HELPERS ====================

  /**
   * Recargar conversaciones
   */
  cargarConversaciones(): void {
    this.obtenerConversaciones().subscribe();
  }

  /**
   * Limpiar mensajes
   */
  limpiarMensajes(): void {
    this.mensajesSubject.next([]);
    this.conversacionActualSubject.next(null);
  }

  /**
   * Agregar un mensaje localmente a la lista actual
   * Usado para actualización optimista después de enviar archivos
   */
  agregarMensajeLocal(mensaje: Mensaje): void {
    const mensajesActuales = this.mensajesSubject.value;
    
    // Evitar duplicados
    const existeMensaje = mensajesActuales.some(m => m.id_mensaje === mensaje.id_mensaje);
    if (!existeMensaje) {
      const nuevosMensajes = [...mensajesActuales, mensaje];
      this.mensajesSubject.next(nuevosMensajes);
      console.log('📨 Mensaje agregado localmente:', mensaje.id_mensaje);
    } else {
      console.log('⚠️ Mensaje duplicado, no se agregó:', mensaje.id_mensaje);
    }
  }

  /**
   * Verificar si un usuario está en línea (desde cache local)
   */
  estaEnLinea(idUsuario: number): boolean {
    return this.usuariosEnLineaSubject.value.has(idUsuario);
  }

  /**
   * Obtener usuarios en línea (desde cache local)
   */
  obtenerUsuariosEnLinea(): Set<number> {
    return this.usuariosEnLineaSubject.value;
  }

  /**
   * Verificar si un usuario está en línea (HTTP - consulta al servidor)
   */
  verificarUsuarioEnLinea(idUsuario: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/usuario/${idUsuario}/en-linea`);
  }

  /**
   * Verificar estado de múltiples usuarios (HTTP - batch)
   */
  verificarUsuariosEnLinea(idsUsuarios: number[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/usuarios/en-linea`, {
      ids_usuarios: idsUsuarios
    });
  }

  /**
   * Obtener todos los usuarios conectados (HTTP)
   */
  obtenerUsuariosConectados(): Observable<any> {
    return this.http.get(`${this.apiUrl}/usuarios-conectados`);
  }

  /**
   * Obtener estado de participantes de una conversación (HTTP)
   */
  obtenerEstadoParticipantes(idConversacion: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/conversacion/${idConversacion}/participantes/estado`);
  }

  /**
   * Cargar estados de usuarios en línea desde el servidor
   * Se llama al inicializar para obtener el estado inicial
   */
  private cargarEstadosUsuariosEnLinea(): void {
    this.obtenerUsuariosConectados().subscribe({
      next: (response) => {
        if (response.ok && response.usuarios_conectados) {
          const idsConectados = response.usuarios_conectados.map((u: any) => u.id_usuario);
          const usuariosEnLinea = new Set<number>(idsConectados);
          
          // Asegurarse de que el usuario actual esté en la lista
          const usuarioActual = this.authService.getCurrentUser();
          if (usuarioActual) {
            usuariosEnLinea.add(usuarioActual.uid);
          }
          
          this.usuariosEnLineaSubject.next(usuariosEnLinea);
          console.log('👥 Estados iniciales de usuarios cargados:', idsConectados.length, 'usuarios en línea');
        }
      },
      error: (error) => {
        console.error('❌ Error al cargar estados de usuarios:', error);
        // En caso de error, al menos agregar el usuario actual
        this.agregarUsuarioActualComoEnLinea();
      }
    });
  }

  /**
   * Agregar el usuario actual como "en línea" al conjunto local
   */
  private agregarUsuarioActualComoEnLinea(): void {
    const usuarioActual = this.authService.getCurrentUser();
    if (usuarioActual) {
      const usuariosEnLinea = this.usuariosEnLineaSubject.value;
      usuariosEnLinea.add(usuarioActual.uid);
      this.usuariosEnLineaSubject.next(new Set(usuariosEnLinea));
      console.log('✅ Usuario actual agregado como en línea:', usuarioActual.uid);
    }
  }
}
